import { useEffect } from "react";
import { PBS_LIVE_WIDGET_ID, isInsidePbsLiveWidget } from "../lib/pbsLiveWidget";

/**
 * Keeps the PBS Live widget typeable while a Radix modal (Dialog/Sheet) is open,
 * on both desktop and mobile.
 *
 * Radix's FocusScope traps focus by listening on `document` for `focusin` AND
 * `focusout`: whenever focus leaves the modal it yanks it back. The widget lives
 * outside every modal, so without this two transitions get hijacked:
 *
 *  1. Focus landing IN the widget fires `focusin` on a widget node. We stop that
 *     from bubbling to `document`, so Radix's `handleFocusIn` never sees it.
 *     `stopPropagation` (not `stopImmediatePropagation`) is deliberate — the
 *     widget's own React listeners live on/inside `#root-pbslive` and still fire.
 *
 *  2. Focus moving FROM a modal element INTO the widget fires `focusout` on the
 *     modal element, inside the modal's own subtree — the listeners above can't
 *     see it, so it reaches Radix's document handler, which reads
 *     `relatedTarget` (the widget input) and yanks focus back. This is the
 *     "click the box but no caret / can't type" symptom. We intercept that
 *     `focusout` in the capture phase (before Radix's bubble-phase handler) and
 *     cancel it, but only when focus is heading into the widget and a modal is
 *     actually open, so normal app focus behaviour is untouched.
 *
 * It also restores SCROLLING inside the widget. Radix wraps modal content in
 * `react-remove-scroll`, which registers `wheel`/`touchmove` blockers on
 * `document` (bubble phase) and `preventDefault()`s any scroll outside the modal
 * content — the widget included. We stop those events at the widget root before
 * they reach `document`, but only while a modal is open, so native scrolling in
 * the widget works again on both desktop (wheel) and mobile (touch).
 *
 * The widget is injected asynchronously by an external script, so we attach as
 * soon as it appears and keep watching in case it remounts.
 */
export function usePbsLiveModalInterop(): void {
  useEffect(() => {
    let widget: HTMLElement | null = null;

    const isModalOpen = () => !!document.querySelector('[role="dialog"][data-state="open"]');
    const stop = (event: Event) => event.stopPropagation();
    // Only shield scroll while a modal is open, so we never interfere with any
    // document-level scroll handling the app does in normal use.
    const stopScrollWhenModalOpen = (event: Event) => {
      if (isModalOpen()) event.stopPropagation();
    };

    const attach = (el: HTMLElement) => {
      if (widget === el) return;
      detach();
      widget = el;
      widget.addEventListener("focusin", stop);
      widget.addEventListener("focusout", stop);
      widget.addEventListener("wheel", stopScrollWhenModalOpen, { passive: true });
      widget.addEventListener("touchmove", stopScrollWhenModalOpen, { passive: true });
    };

    const detach = () => {
      if (!widget) return;
      widget.removeEventListener("focusin", stop);
      widget.removeEventListener("focusout", stop);
      widget.removeEventListener("wheel", stopScrollWhenModalOpen);
      widget.removeEventListener("touchmove", stopScrollWhenModalOpen);
      widget = null;
    };

    // Capture-phase shield for focus moving out of a modal and into the widget.
    const onFocusOutCapture = (event: FocusEvent) => {
      if (isInsidePbsLiveWidget(event.relatedTarget) && isModalOpen()) {
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener("focusout", onFocusOutCapture, true);

    const existing = document.getElementById(PBS_LIVE_WIDGET_ID);
    if (existing) attach(existing);

    const observer = new MutationObserver(() => {
      const el = document.getElementById(PBS_LIVE_WIDGET_ID);
      if (el && el !== widget) attach(el);
      else if (!el && widget) detach();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("focusout", onFocusOutCapture, true);
      observer.disconnect();
      detach();
    };
  }, []);
}
